import React, { useEffect, useRef, useState } from 'react';

import './BubbleChart.scss';
import { IBubbleChart, IBubbleDataWithRadius } from '../../../../interfaces/charts';
import { toLocaleNumberFormat } from '../../../../utils/utils';
import BdsTooltip from '../../BdsTooltip/BdsTooltip';

const MAX_RADIUS = 182;
const ONE_DEGREE = Math.PI / 180;

const BubbleChart = ({ data, colorScheme, unitsName, isPercentage = false, isExpanded, extraData }: IBubbleChart) => {
  const containerRef = useRef(null);
  const [sortedData, setSortedData] = useState<IBubbleDataWithRadius[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [maxValueIndex, setMaxValueIndex] = useState(-1);

  const calculateRadii = () => {
    const filteredData = data.filter((item) => item.value > 0);
    const maxValue = Math.max(...filteredData.map((item) => item.value));
    const minValue = Math.min(...filteredData.map((item) => item.value));
    const minRadius = MAX_RADIUS / 2;

    const dataWithRadii = filteredData.map((item) => {
      const radius =
        minValue >= maxValue * 0.25
          ? Math.floor(MAX_RADIUS * Math.sqrt(item.value / maxValue))
          : Math.floor(minRadius * (1 + Math.sqrt((item.value - minValue) / (maxValue - minValue))));
      return { ...item, radius };
    });

    setSortedData([...dataWithRadii].sort((a, b) => b.value - a.value));
  };

  const positionCircles = () => {
    if (!containerRef.current) return;

    const container: HTMLElement = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;
    const circleAmount = container.childNodes.length;

    container.childNodes.forEach((circle, index) => {
      if (!(circle instanceof HTMLElement)) return;
      const radius = circle.offsetWidth / 2;

      const distanceCoeficientByNumberOfCircles = 40;
      const distanceCoeficientByCircleRadius = 1.2;
      const distanceFromCenter =
        centerX +
        (radius - MAX_RADIUS) * distanceCoeficientByCircleRadius -
        (distanceCoeficientByNumberOfCircles * (4 - circleAmount)) / circleAmount;

      const angleOffset = circleAmount !== 2 ? 20 * ONE_DEGREE : -20 * ONE_DEGREE;
      const angle = index * (360 / sortedData.length) * ONE_DEGREE + angleOffset;

      const x = centerX + distanceFromCenter * Math.cos(angle) - radius;
      const y = centerY + distanceFromCenter * Math.sin(angle) - radius;

      circle.style.left = `${x}px`;
      circle.style.top = `${y}px`;
    });
  };

  useEffect(() => {
    calculateRadii();
  }, [data]);

  useEffect(() => {
    window.addEventListener('resize', positionCircles);
    positionCircles();

    return () => {
      window.removeEventListener('resize', positionCircles);
    };
  }, [sortedData]);

  useEffect(() => {
    if (sortedData.length > 0) {
      setMaxValueIndex(
        sortedData
          .slice()
          .sort((a, b) => b.value - a.value)
          .findIndex((item) => item.value > maxValueIndex)
      );
    }
  }, [maxValueIndex, sortedData]);

  const tooltipPosition = (index: number): 'offset-right' | 'offset-left' => {
    const totalItems = sortedData.length;

    if (totalItems === 2) {
      return index === 0 ? 'offset-right' : 'offset-left';
    }

    if (totalItems === 3) {
      return index === 0 || index === 2 ? 'offset-right' : 'offset-left';
    }

    if (totalItems === 4) {
      return index === 0 || index === 3 ? 'offset-right' : 'offset-left';
    }

    return 'offset-right';
  };

  return (
    <div className={`bubble-chart ${colorScheme}`}>
      <div
        className={`bubbles-container ${isExpanded ? 'expanded' : ''} ${
          extraData?.length ? 'is-expandable' : ''
        } lenght-${extraData?.length}`}
        ref={containerRef}
      >
        {sortedData.map((item, index) => (
          <button
            key={item.name}
            className={`bubble color-${index} ${index === maxValueIndex ? 'max-value' : ''} ${
              hoveredIndex !== null && hoveredIndex !== index ? 'hover-inactive' : ''
            }`}
            style={{ width: item.radius, height: item.radius }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div className="bubble-icon">
              <img src={item.icon} alt={item.name} />
            </div>
            <div className="bubble-text-container">
              <div className={`value color-${index}`}>
                {toLocaleNumberFormat(item.value)}
                {isPercentage && '%'}
                <BdsTooltip
                  open={hoveredIndex === index}
                  className={`tooltip ${tooltipPosition(index)} ${index === 3 ? 'min-value' : ''}`}
                  message={item.name}
                  subtitle={item.description}
                  position={tooltipPosition(index)}
                  containerShape="rounded"
                  showPointer={false}
                >
                  <></>
                </BdsTooltip>
              </div>
              {index === 0 && <div className="value-unit">{unitsName} </div>}
            </div>
          </button>
        ))}
      </div>
      <div className={'bubble-chart-legend flex flex-row justify-center items-center'}>
        <div
          className={`legend-container flex flex-row justify-center items-start ${isExpanded ? 'expanded' : ''} ${
            extraData?.length ? 'is-expandable' : ''
          }`}
        >
          {sortedData.map((item, index) => (
            <div key={item.name} className="legend-item flex flex-row justify-start items-start">
              <div className={`legend-circle color-${index}`}></div>
              <div className="legend-name">{item.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BubbleChart;
